import {
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenu,
  Typography,
  Avatar,
  Button,
} from '@ui/components';
import { MenuVertical, ArrowDown, ArrowUp, Edit } from '@ui/components/icons';
import { useCallback, useState, Fragment, FC } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HasPermission } from '@shared/components';
import { languages } from '@shared/global/i18n';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@shared/hooks';
import { Routes } from '@shared/enums';

import { CompanyAccountManagementSidebar } from '../company-account-management';
import { LogoutDialog } from './logout-dialog/logout-dialog';
import { useUserSettings } from './use-user-settings';
import { CurrentUserSidebar } from '../current-user';

interface UserSettingsProps {
  sidebarOpen?: boolean;
}

export const UserSettings: FC<UserSettingsProps> = ({ sidebarOpen = true }) => {
  const { setUserLanguage } = useCurrentUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { goToLogout, setUserCompany, user, canSeeCompanyDetails } = useUserSettings();

  const [expandedCompanies, setExpandedCompanies] = useState<string[]>([]);
  const [isLogoutDialogOpened, setIsLogoutDialogOpened] = useState(false);
  const [isUserEditOpened, setIsUserEditOpened] = useState(false);
  const [openedCompanyAccountId, setOpenedCompanyAccountId] = useState<string | false>(false);

  const userName = `${user?.firstName} ${user?.lastName}`;
  const toggleExpandedCompany = (companyId: string) => {
    setExpandedCompanies((prev) => {
      if (prev.includes(companyId)) return prev.filter((id) => id !== companyId);
      return [...prev, companyId];
    });
  };

  const onCompanyChange = useCallback(
    (companyId: string) => {
      queryClient.clear();
      setUserCompany(companyId);
    },
    [queryClient, setUserCompany],
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="flex justify-between items-center gap-2 pl-2 pr-0 h-14 overflow-hidden"
            variant="ghost"
          >
            <Avatar fallback={userName} />
            {sidebarOpen && (
              <>
                <section className="truncate text-left">
                  <Typography variant="labelMediumMedium" className="text-default">
                    {userName}
                  </Typography>
                  <Typography className="truncate max-w-48" variant="labelSmallBook">
                    {user?.company?.name}
                  </Typography>
                </section>
                <MenuVertical className="mx-2" />
              </>
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="min-w-64 px-2" side="right" align="end">
          <div className="flex items-center p-2 pr-0.5 gap-2">
            <Avatar fallback={userName} />
            <Typography variant="labelMediumMedium" className="text-default">
              {userName}
            </Typography>

            <HasPermission permissionFeature="USER_ACCOUNT_EDIT" permissionType="READ">
              <Button
                onClick={() => setIsUserEditOpened(true)}
                className="ml-auto"
                variant="ghost"
                size="small"
              >
                <Edit />
              </Button>
            </HasPermission>
          </div>
          <DropdownMenuSeparator />

          {user?.companies.map((company) => (
            <Fragment key={company.id}>
              <div className="flex items-center my-1 pr-0.5 gap-2">
                <Button
                  onClick={() => onCompanyChange(company.id)}
                  className="justify-start m-0 rounded-sm"
                  variant="ghost"
                  size="large"
                  wide
                >
                  <Avatar fallback={company.name} />
                  <div className="text-left">
                    <Typography
                      className="text-default max-w-48 truncate"
                      variant="labelMediumMedium"
                    >
                      {company.name}
                    </Typography>
                    <Typography variant="labelSmallBook" className="text-weak">
                      {t(`AUTH.ROLE.${company.role}`)}
                    </Typography>
                  </div>
                </Button>

                {canSeeCompanyDetails && (
                  <Button
                    onClick={() => toggleExpandedCompany(company.id)}
                    className="ml-auto"
                    variant="ghost"
                    size="small"
                  >
                    {expandedCompanies.includes(company.id) ? <ArrowUp /> : <ArrowDown />}
                  </Button>
                )}
              </div>

              {expandedCompanies.includes(company.id) && (
                <>
                  <HasPermission
                    permissionFeature="COMPANY_ACCOUNT_LIST_OF_USERS"
                    forRole={company.role}
                    permissionType="READ"
                  >
                    <DropdownMenuItem
                      onClick={() => navigate(`/${company.id}/${Routes.USERS}`)}
                      className="text-weak"
                    >
                      {t('NAVBAR.USERS')}
                    </DropdownMenuItem>
                  </HasPermission>

                  <HasPermission
                    permissionFeature="COMPANY_ACCOUNT_EDIT"
                    forRole={company.role}
                    permissionType="READ"
                  >
                    <DropdownMenuItem
                      onClick={() => setOpenedCompanyAccountId(company.id)}
                      className="text-weak"
                    >
                      {t('NAVBAR.ACCOUNT_MANAGEMENT')}
                    </DropdownMenuItem>
                  </HasPermission>
                </>
              )}
            </Fragment>
          ))}

          <DropdownMenuSeparator />

          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="text-weak">
              {t('NAVBAR.LANGUAGE')}
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                {languages.map((language) => (
                  <DropdownMenuItem onClick={() => setUserLanguage(language)} key={language}>
                    <img
                      src={`${import.meta.env.VITE_BASE_PATH}/locales/${language}/flag.svg`}
                      className="mr-4 h-4"
                      alt={language}
                    />
                    <span>{t(`NAVBAR.LANGUAGE_${language.toUpperCase()}`)}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>

          <DropdownMenuItem onClick={() => setIsLogoutDialogOpened(true)} className="text-weak">
            {t('NAVBAR.LOGOUT')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CurrentUserSidebar onIsOpenedChange={setIsUserEditOpened} isOpened={isUserEditOpened} />
      <CompanyAccountManagementSidebar
        onClose={() => setOpenedCompanyAccountId(false)}
        companyId={openedCompanyAccountId || ''}
        isOpened={!!openedCompanyAccountId}
      />
      <LogoutDialog
        setIsOpen={setIsLogoutDialogOpened}
        isOpen={isLogoutDialogOpened}
        onLogout={goToLogout}
      />
    </>
  );
};
